/**
 * Cash discount pricing factor.
 *
 * When cash discount is enabled, listed (non-cash) prices are divided by this
 * factor to get the cash price, and cash prices are multiplied by it to get the
 * non-cash price. Historically this was hardcoded to 1.04 (a 4% differential).
 *
 * It can now be overridden per terminal via the `cash_discount_rate` db_setting.
 * The setting stores the factor directly, e.g. "1.03" for a 3% differential.
 * Any value that is not a finite number in the [1, 2] range is ignored and the
 * default is used.
 */
import { Decimal } from "decimal.js";

export const DEFAULT_CASH_DISCOUNT_FACTOR = 1.04;

/**
 * @param {Array<{name: string, value: *}>} dbSettings terminal db_settings array
 * @returns {number} the cash discount factor to use
 */
export function getCashDiscountFactor(dbSettings) {
  const raw = Array.isArray(dbSettings)
    ? dbSettings.find((setting) => setting && setting.name === "cash_discount_rate")?.value
    : undefined;

  const parsed = parseFloat(raw);
  if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 2) {
    return parsed;
  }
  return DEFAULT_CASH_DISCOUNT_FACTOR;
}

const toCash = (value, factor) => new Decimal(value || 0).dividedBy(factor).toNumber();
const toList = (value, factor) => new Decimal(value || 0).times(factor).toNumber();

/**
 * Bring one order line's stored prices into the representation required by the
 * order's current `cash_discount` state, in place. Idempotent: every line
 * carries a `cd_adjusted` marker so calling this repeatedly (e.g. every time the
 * modifier modal closes) is a no-op once the line is already converted.
 *
 * Modifiers are converted on BOTH `price` and `price_original` because the
 * pricing engine (`getModifierEffectivePrice`) and the tax calc read
 * `price_original` first and only fall back to `price`; adjusting `price` alone
 * leaves modifiers at full menu price in the totals.
 *
 * Legacy lines with no marker are assumed to already match `want`, except a
 * brand-new line, whose stored price is always the plain list price.
 *
 * @param {object} item order item (mutated)
 * @param {object} opts
 * @param {number} opts.factor cash discount factor (> 1)
 * @param {boolean} opts.want whether the order currently has cash discount on
 * @param {boolean} [opts.newItem=false] the item was just added this call
 * @returns {object} the same item
 */
export function applyCashDiscountToItem(item, { factor, want, newItem = false } = {}) {
  if (!item || !(factor > 1)) return item;

  const isAdjusted = (line) =>
    newItem ? false : (line.cd_adjusted === undefined ? want : !!line.cd_adjusted);

  if (want && !isAdjusted(item)) item.price = toCash(item.price, factor);
  else if (!want && isAdjusted(item)) item.price = toList(item.price, factor);
  item.cd_adjusted = want;
  item.price_base = item.price;

  (item.modifiers || []).forEach((mod) => {
    const adjusted = isAdjusted(mod);
    if (want && !adjusted) {
      if (mod.price_original != null) mod.price_original = toCash(mod.price_original, factor);
      mod.price = toCash(mod.price, factor);
    } else if (!want && adjusted) {
      if (mod.price_original != null) mod.price_original = toList(mod.price_original, factor);
      mod.price = toList(mod.price, factor);
    }
    mod.cd_adjusted = want;
  });

  return item;
}
